import { FC, useEffect, useRef, useState, useCallback } from "react";
import { Grid, Box, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import TouchAppIcon from "@mui/icons-material/TouchApp";

import CustomerHistoryTableRow from "./customer-history-table-row";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { getCustomerBillHistoryAction } from "../../../../redux/actions/customerAction";
import InvoiceHistoryModal from "../../../pos/invoice-history-modal/invoice-history-modal";
import { ICustomerBill, ICustomerBillsResponse } from "../../../../typings";
import {
  addSession,
  getBillByIdAction,
  getBillItemIdListAction,
  updateCartSessionItem,
} from "../../../../redux/actions/cartAction";
import { Colors } from "../../../../configs";
import { ICartSession } from "../../../../typings/interfaces/pos/cart-session";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import {
  BillFilterDateType,
  BillPaymentStatus,
  BillPickupStatus,
} from "../../../../typings/enum/paymentTypes";
import dayjs from "dayjs";

export interface ICustomerHistoryTableProps {
  height?: string;
  contentHeight?: string;
  paymentFilterStatus: BillPaymentStatus | null;
  filterDateType: BillFilterDateType;
  startDate: string | undefined;
  endDate: string | undefined;
  pickingFilterStatus?: BillPickupStatus | null;
  searchQuery?: string;
}

const CustomerHistoryTable: FC<ICustomerHistoryTableProps> = ({
  height = "28.42rem",
  contentHeight = "39.07rem",
  paymentFilterStatus,
  filterDateType,
  startDate,
  endDate,
  pickingFilterStatus,
  searchQuery,
}) => {
  const params = useParams();
  const customerId = params.id;
  const queryParams = new URLSearchParams(window.location.search);
  const billId = queryParams.get("billId");
  const dispatch = useAppDispatch();
  const { bills, billsLoading } = useAppSelector((state) => state.customer);
  const { refreshCustomerHistory, cartHistorySelectedRows } = useAppSelector(
    (state) => state.cart
  );
  const [clickedRow, setClickedRow] = useState<ICustomerBill | null>(null);
  const snackbar = useSnackbarContext();

  const [openModal, setOpenModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<ICustomerBill | null>(
    null
  );

  const [allLoadedItems, setAllLoadedItems] = useState<ICustomerBill[]>([]);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const tableRef = useRef<HTMLDivElement>(null);

  const handleOnClickRow = (data: ICustomerBill) => {
    if (clickedRow?.id === data.id) return;

    setClickedRow(data);
    dispatch(CartSliceAction.clearSession());

    if (data.billString) {
      dispatch(addSession(JSON.parse(data.billString)));
    } else {
      snackbar.showSnackbar("cart not found for the bill", "error");
    }
  };

  const handleScroll = useCallback(() => {
    if (tableRef.current && !loadingNextPage && hasMoreData && !billsLoading) {
      const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (isNearBottom) {
        loadNextPage();
      }
    }
  }, [loadingNextPage, hasMoreData, billsLoading]);

  const loadNextPage = useCallback(async () => {
    if (loadingNextPage || !hasMoreData || billsLoading) return;

    setLoadingNextPage(true);
    await getCustomerBillList(currentPage + 1, false);
  }, [loadingNextPage, hasMoreData, billsLoading, currentPage]);

  useEffect(() => {
    if (billId && allLoadedItems.length > 0) {
      const matched = allLoadedItems.find((bill) => bill.billId === billId) || null;
      setClickedRow(matched);
      if (matched?.billString) {
        dispatch(CartSliceAction.clearSession());
        dispatch(addSession(JSON.parse(matched.billString)));
      }
    }
  }, [billId, allLoadedItems]);

  useEffect(() => {
    if (clickedRow?.id) {
      dispatch(getBillByIdAction({ billId: String(clickedRow?.id) }));
    }
  }, [clickedRow, dispatch]);

  useEffect(() => {
    setAllLoadedItems([]);
    setCurrentPage(0);
    setHasMoreData(true);
    setIsInitialLoad(true);
    getCustomerBillList(0, true);
  }, [
    customerId,
    paymentFilterStatus,
    pickingFilterStatus,
    filterDateType,
    startDate,
    endDate,
    searchQuery,
  ]);

  const refreshAlreadyOpenedPages = useCallback(async () => {
    if (refreshCustomerHistory && currentPage >= 0) {
      const tempItems: ICustomerBill[] = [];
      for (let i = 0; i <= currentPage; i++) {
        const res = await getCustomerBillListForRefresh(i);
        if (res && res.content) {
          tempItems.push(...res.content);
        }
      }
      setAllLoadedItems(tempItems);

      // cartHistorySelectedRows drives the Edit/Void button state and payloads
      // on the parent page. Without this, it keeps holding the pre-void/
      // refund/pickup snapshot of the bill (e.g. isVoided still false), so
      // those actions stay wrongly enabled/disabled after this refresh.
      if (cartHistorySelectedRows.length > 0) {
        const refreshedSelectedRows = cartHistorySelectedRows
          .map((row) => tempItems.find((item) => item.billId === row.billId))
          .filter((item): item is ICustomerBill => Boolean(item));
        dispatch(
          CartSliceAction.setCartHistorySelectedRows(refreshedSelectedRows)
        );
        // Per-item pickup/paid status (billItemIdList) also goes stale after
        // a payment/pickup - without this, Edit Invoice's "does this bill
        // have an unattributed payment" check and the per-item lock in Edit
        // Invoice itself both keep judging off pre-payment data.
        refreshedSelectedRows.forEach((row) => {
          dispatch(getBillItemIdListAction(row.id));
        });
      }

      dispatch(CartSliceAction.setRefreshCustomerHistory(false));
    }
  }, [refreshCustomerHistory, currentPage, dispatch, cartHistorySelectedRows]);

  useEffect(() => {
    if (refreshCustomerHistory) {
      refreshAlreadyOpenedPages();
    }
  }, [refreshCustomerHistory, refreshAlreadyOpenedPages]);

  const getCustomerBillListForRefresh = async (
    page: number
  ): Promise<ICustomerBillsResponse | null> => {
    if (!customerId) return null;

    const hasDateRange = !!startDate && !!endDate;

    const res = await dispatch(
      getCustomerBillHistoryAction({
        customerId: Number(customerId),
        invoiceNumber: searchQuery,
        paymentFilterStatus: paymentFilterStatus ?? undefined,
        pickingFilterStatus: pickingFilterStatus ?? undefined,
        ...(hasDateRange && {
          filterDateType,
          startDate: dayjs(startDate).format("YYYY-MM-DD"),
          endDate: dayjs(endDate).format("YYYY-MM-DD"),
        }),
        page: page,
        size: 10,
      })
    );

    return res.payload as ICustomerBillsResponse;
  };

  const getCustomerBillList = async (
    page: number = 0,
    isReset: boolean = false
  ) => {
    if (!customerId) return;

    const hasDateRange = !!startDate && !!endDate;

    if (isReset || page === 0) {
      await dispatch(CartSliceAction.setCartHistorySelectedRows([]));
      dispatch(CartSliceAction.clearSession());
      setClickedRow(null);
    }

    try {
      const res = await dispatch(
        getCustomerBillHistoryAction({
          customerId: Number(customerId),
          invoiceNumber: searchQuery,
          paymentFilterStatus: paymentFilterStatus ?? undefined,
          pickingFilterStatus: pickingFilterStatus ?? undefined,
          ...(hasDateRange && {
            filterDateType,
            startDate: dayjs(startDate).format("YYYY-MM-DD"),
            endDate: dayjs(endDate).format("YYYY-MM-DD"),
          }),
          page: page,
          size: 10,
        })
      );

      const responseData = res.payload as ICustomerBillsResponse;

      if (responseData?.content) {
        if (isReset || page === 0) {
          setAllLoadedItems(responseData.content);
        } else {
          setAllLoadedItems((prev) => [...prev, ...responseData.content]);
        }

        const totalPages = responseData.totalPages ?? 1;
        const responseCurrentPage = responseData.pageable.pageNumber ?? 0;
        setCurrentPage(responseCurrentPage);
        setHasMoreData(responseCurrentPage < totalPages - 1);
      } else {
        if (isReset || page === 0) {
          setAllLoadedItems([]);
        }
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error loading customer bill list:", error);
      if (isReset || page === 0) {
        setAllLoadedItems([]);
      }
      setHasMoreData(false);
    } finally {
      setLoadingNextPage(false);
      setIsInitialLoad(false);
    }
  };

  const styles = {
    tableHeadingCell: {
      borderRadius: "0",
      background: Colors.SECONDARY,
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      padding: "0.5rem 0.71rem",
      boxSizing: "border-box",
      color: "white",
      fontWeight: 600,
      height: "2.92857142857rem",
    },
  };

  const handleViewClick = (data: ICustomerBill) => {
    setSelectedInvoice(data);
    setOpenModal(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: Colors.WHITE,
        border: `1px solid ${Colors.ACCENT_GRAY}`,
        borderRadius: "0.93rem",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        overflow: "hidden",
        gap: "1.14rem",
        height,
      }}
    >
      <Box
        sx={{
          flex: 4,
          minWidth: "100%",
          maxWidth: "100% !important",
          overflowX: "hidden",
          overflowY: "hidden",
        }}
      >
        <Grid container sx={{ gap: "0.21rem", height: "100%" }}>
          <Grid item xs={12}>
            <Box
              style={{
                position: "sticky",
                top: 0,
                boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ display: "flex", gap: "0.15rem", flexWrap: "nowrap" }}>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: "2.92857142857rem",
                    maxWidth: "2.92857142857rem !important",
                    borderTopLeftRadius: "0.71rem",
                  }}
                >
                  &nbsp;
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: { lg: "6.4rem", md: "6.4rem", sm: "6.4rem" },
                    fontSize: "1rem",
                  }}
                >
                  Invoice
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: "6.2rem",
                    fontSize: "1rem",
                  }}
                >
                  Date In
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: { lg: "5.5rem", md: "5.5rem" },
                    fontSize: "1rem",
                  }}
                >
                  Cashier
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: "5.9rem",
                    fontSize: "1rem",
                  }}
                >
                  Due date
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: "5.606rem",
                    fontSize: "1rem",
                  }}
                >
                  Pieces
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: "4.606rem",
                    fontSize: "1rem",
                  }}
                >
                  Rack
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: "5.5rem",
                    fontSize: "1rem",
                  }}
                >
                  Balance
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: "5.9rem",
                    fontSize: "1rem",
                  }}
                >
                  Pay Date
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: "5.9rem",
                    fontSize: "1rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  Pickup Date
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1,
                    minWidth: "6.2rem",
                    fontSize: "1rem",
                  }}
                >
                  Status
                </Box>
                <Box
                  sx={{
                    ...styles.tableHeadingCell,
                    flex: 1.5,
                    minWidth: "5.246rem",
                    maxWidth: "5.246rem !important",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderTopRightRadius: "0.71rem",
                  }}
                >
                  <TouchAppIcon sx={{ color: "white" }} />
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ height: contentHeight }}>
            <Box
              sx={{
                height: contentHeight,
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: { lg: "0.5rem", md: "0.3rem", sm: "0rem" },
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ADADAD",
                  borderRadius: "0rem",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                  borderRadius: "0rem",
                },
              }}
              ref={tableRef}
              onScroll={handleScroll}
            >
              {!isInitialLoad &&
                allLoadedItems.length > 0 &&
                allLoadedItems.map((item, index) => (
                  <CustomerHistoryTableRow
                    key={`${item.id}-${index}`}
                    isSelected={clickedRow?.id === item.id}
                    data={item}
                    firstRow={index === 0}
                    lastRow={index === allLoadedItems.length - 1}
                    onClick={handleOnClickRow}
                    onViewClick={handleViewClick}
                  />
                ))}

              {loadingNextPage && allLoadedItems.length > 0 && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    py: "1rem",
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}

              {!billsLoading &&
                !isInitialLoad &&
                allLoadedItems.length === 0 && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      py: "1rem",
                      color: Colors.SECONDARY,
                    }}
                  >
                    No bills found
                  </Box>
                )}

              {(billsLoading || isInitialLoad) &&
                allLoadedItems.length === 0 && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress
                      sx={{
                        mx: "auto",
                        mt: "7rem",
                      }}
                    />
                  </Box>
                )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {selectedInvoice && (
        <InvoiceHistoryModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          selectedId={selectedInvoice.id}
          billId={selectedInvoice.billId ?? ""}
          customerId={customerId ?? ""}
        />
      )}
    </Box>
  );
};

export default CustomerHistoryTable;
